const hasText = (value) => String(value ?? '').trim().length > 0;

export const validatePlanDetails = (state) => {
    const fields = {};

    if (!hasText(state.title)) fields.title = 'nutrition.validation.nameRequired';
    if (!hasText(state.description)) {
        fields.description = 'nutrition.validation.descriptionRequired';
    }
    if (
        !Number.isInteger(Number(state.durationDays)) ||
        Number(state.durationDays) < 1 ||
        Number(state.durationDays) > 365
    ) {
        fields.durationDays = 'nutrition.validation.durationInvalid';
    }

    return fields;
};

export const validateNutritionPlan = (state) => {
    const issues = [];
    const detailFields = validatePlanDetails(state);

    Object.values(detailFields).forEach((messageKey) => issues.push({ messageKey }));

    const duration = Number(state.durationDays);
    if (Number.isInteger(duration) && duration > 0 && state.days.length !== duration) {
        issues.push({ messageKey: 'nutrition.validation.daysMismatch' });
    }

    const dayNumbers = state.days.map((day) => Number(day.dayNumber));
    if (new Set(dayNumbers).size !== dayNumbers.length) {
        issues.push({ messageKey: 'nutrition.validation.duplicateDays' });
    }

    const optionIds = state.days.flatMap((day) =>
        day.meals.flatMap((meal) => meal.options.map((option) => String(option.optionId)))
    );
    if (new Set(optionIds).size !== optionIds.length) {
        issues.push({ messageKey: 'nutrition.validation.duplicateOptionIds' });
    }

    state.days.forEach((day) => {
        if (!hasText(day.objective)) {
            issues.push({
                messageKey: 'nutrition.validation.dayObjectiveRequired',
                values: { day: day.dayNumber },
                dayId: day._uiId
            });
        }
        if (day.meals.length === 0) {
            issues.push({
                messageKey: 'nutrition.validation.dayMealRequired',
                values: { day: day.dayNumber },
                dayId: day._uiId
            });
        }

        day.meals.forEach((meal, mealIndex) => {
            const values = { day: day.dayNumber, meal: mealIndex + 1 };
            if (!hasText(meal.mealType) || !hasText(meal.title)) {
                issues.push({
                    messageKey: 'nutrition.validation.mealDetailsRequired',
                    values,
                    dayId: day._uiId
                });
            }
            if (meal.options.length === 0) {
                issues.push({
                    messageKey: 'nutrition.validation.mealOptionRequired',
                    values,
                    dayId: day._uiId
                });
            }

            meal.options.forEach((option, optionIndex) => {
                const optionValues = { ...values, option: optionIndex + 1 };
                if (option.items.length === 0) {
                    issues.push({
                        messageKey: 'nutrition.validation.optionIncomplete',
                        values: optionValues,
                        dayId: day._uiId
                    });
                    return;
                }
                if (
                    option.items.some(
                        (item) => !hasText(item.name) || !hasText(item.quantity)
                    )
                ) {
                    issues.push({
                        messageKey: 'nutrition.validation.foodIncomplete',
                        values: optionValues,
                        dayId: day._uiId
                    });
                }
            });
        });
    });

    state.foodsToAvoid.forEach((group) => {
        if (
            !hasText(group.category) ||
            group.items.length === 0 ||
            group.items.some((item) => !hasText(item.value))
        ) {
            issues.push({ messageKey: 'nutrition.validation.restrictionIncomplete' });
        }
        const normalizedItems = group.items.map((item) =>
            String(item.value).trim().toLocaleLowerCase()
        );
        if (new Set(normalizedItems).size !== normalizedItems.length) {
            issues.push({ messageKey: 'nutrition.validation.duplicateRestrictionItems' });
        }
    });

    const normalizedCategories = state.foodsToAvoid.map((group) =>
        String(group.category).trim().toLocaleLowerCase()
    );
    if (new Set(normalizedCategories).size !== normalizedCategories.length) {
        issues.push({
            messageKey: 'nutrition.validation.duplicateRestrictionCategories'
        });
    }

    if (state.customNotes.some((note) => !hasText(note.value))) {
        issues.push({ messageKey: 'nutrition.validation.noteIncomplete' });
    }
    const normalizedNotes = state.customNotes.map((note) =>
        String(note.value).trim().toLocaleLowerCase()
    );
    if (new Set(normalizedNotes).size !== normalizedNotes.length) {
        issues.push({ messageKey: 'nutrition.validation.duplicateNotes' });
    }

    return issues;
};
